import { PropType } from 'vue';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import i18n from '@/core/translations/i18n';
import { mapGetters, mapState } from 'vuex';
import { cloneObject, getMxServerSettingsLabel, validLanguages } from '@/core/utils/utils';
import { AccountInfo } from '@azure/msal-browser';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  Language,
  CameraType,
  IntercomType,
  PlantDetailDTO,
  CameraFunction,
  ActivationType,
  IntercomFunction,
  ActivationFunction,
  PlantC300EOSSettingsDTO,
  ServerModel,
} from '@/../client-generator/generated-clients/plants';
import { FETCH_PLANT } from '@/core/utils/emitter-events';
import { SET_APP_LOADING } from '@/core/store/action-types';
import TabsEnumFilter from '@/views/components/TabsEnumFilter/TabsEnumFilter.vue';
import ModalAddEditVdeFunction from './ModalAddEditVdeFunction/ModalAddEditVdeFunction.vue';
import ToggleSwitchButton from '@/views/components/ToggleSwitchButton/ToggleSwitchButton.vue';
import TimeSelectorComponent from '../PlantScenes/TimeSelectorComponent/TimeSelectorComponent.vue';
import DialogConfirmChangeLanguage from './DialogConfirmChangeLanguage/DialogConfirmChangeLanguage.vue';
import WeekDaysSelectorComponent from '../PlantScenes/WeekDaysSelectorComponent/WeekDaysSelectorComponent.vue';
import { SeasonChangeAutoType } from '@/../client-generator/generated-clients/device-settings';

const CAMERA_TYPE = 'CameraType';
const INTERCOM_TYPE = 'IntercomType';
const ACTIVATION_TYPE = 'ActivationType';

let saved_c300eosSettings: PlantC300EOSSettingsDTO | null = null;
@Options({
  name: 'PlantC300EOSSettings',
  emits: ['add-object', 'add-structure', 'update-list'],
  props: {
    currentPlant: {
      required: false,
      type: Object as PropType<PlantDetailDTO>,
    },
  },
  components: {
    Dropdown,
    InputText,
    TabsEnumFilter,
    ToggleSwitchButton,
    TimeSelectorComponent,
    ModalAddEditVdeFunction,
    WeekDaysSelectorComponent,
    DialogConfirmChangeLanguage,
  },
  watch: {
    c300eosSettings: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue && newValue) {
          if (
            JSON.stringify(saved_c300eosSettings) === JSON.stringify(newValue)
          )
            this.isSettingsDirty = false;
          else this.isSettingsDirty = true;
        }
      },
    },
  },
  computed: {
    ...mapState(['account']),
    ...mapGetters('UI', ['plantC300EosSettingsEditEnabled']),
  },
})
export default class PlantC300EOSSettings extends Vue {
  account!: AccountInfo;
  Language = Language;
  CameraType = CameraType;
  CAMERA_TYPE = CAMERA_TYPE;
  IntercomType = IntercomType;
  INTERCOM_TYPE = INTERCOM_TYPE;
  currentPlant?: PlantDetailDTO;
  ActivationType = ActivationType;
  ACTIVATION_TYPE = ACTIVATION_TYPE;
  c300eosSettings: PlantC300EOSSettingsDTO | null = null;
  plantC300EosSettingsEditEnabled!: boolean;

  selectedTab = '0';
  isSettingsDirty = false;
  languageChangerVisibility = false;
  selectedVdeFunction: Object = null!;

  SeasonChangeAutoType = SeasonChangeAutoType;
  validLanguages = validLanguages;

  get isSelectVdeFunction() {
    return !!this.selectedVdeFunction;
  }

  get backLink() {
    return {
      name: 'page-plant-dashboard',
      params: this.$route.params,
    };
  }

  get mxServerLabel() {
    return getMxServerSettingsLabel(this.currentPlant);
  }

  get hiddenTabs() {
    if (this.c300eosSettings?.isThermoExternalCentralUnit) return [2];
    return [];
  }

  tabsFilters = [
    i18n.global.t('plantC300EOSSettings.GeneralSettings'),
    i18n.global.t('AppResource.DoorEntrySystem'),
    i18n.global.t('AppResource.Thermosystem'),
  ];

  mounted() {
    this.fetchPlantC300EOSSettings();
  }

  timezone() {
    const offset = new Date().getTimezoneOffset();
    const minutes = Math.abs(offset);
    const hours = Math.floor(minutes / 60);
    const prefix = offset < 0 ? '+' : '-';
    return prefix + hours;
  }

  async fetchPlantC300EOSSettings() {
    if (!this.currentPlant || !this.currentPlant.desktopId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const _c300eosSettings = await plantsClient.c300eosSettingsGET(
        this.currentPlant.desktopId,
        undefined
      );
      if (_c300eosSettings && _c300eosSettings.gwId) {
        saved_c300eosSettings = cloneObject(_c300eosSettings);
        this.c300eosSettings = _c300eosSettings;
      }
    } catch (error) {
      switch ((error as any).status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  getParsedDate(date?: Date) {
    if (!date) return '';
    const options: any = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Date(date).toLocaleString('en-GB', options);
  }

  showChangeLanguageConfirm() {
    this.languageChangerVisibility = true;
  }

  hideConfirm() {
    this.languageChangerVisibility = false;
  }

  hideSelectedVdeFunction() {
    this.selectedVdeFunction = null!;
  }

  upsertVDEFunction(VDEFunc: any) {
    if (!this.c300eosSettings) {
      console.error(
        `Could not upsert VDE function due to C300E settings missing`
      );
      return;
    }

    this.c300eosSettings.vdeCameraFunctions =
      this.c300eosSettings.vdeCameraFunctions ?? [];
    this.c300eosSettings.vdeIntercomFunctions =
      this.c300eosSettings.vdeIntercomFunctions ?? [];
    this.c300eosSettings.vdeActivationFunctions =
      this.c300eosSettings.vdeActivationFunctions ?? [];

    let idxOf = -1;
    switch (VDEFunc.type) {
      case INTERCOM_TYPE:
        idxOf = this.c300eosSettings!.vdeIntercomFunctions.findIndex(
          (e: any) => e._key === VDEFunc.ref._key
        );
        idxOf === -1
          ? this.c300eosSettings!.vdeIntercomFunctions.push(VDEFunc.ref)
          : (this.c300eosSettings!.vdeIntercomFunctions[idxOf] = VDEFunc.ref);
        break;
      case CAMERA_TYPE:
        idxOf = this.c300eosSettings!.vdeCameraFunctions.findIndex(
          (e: any) => e._key === VDEFunc.ref._key
        );
        idxOf === -1
          ? this.c300eosSettings!.vdeCameraFunctions.push(VDEFunc.ref)
          : (this.c300eosSettings!.vdeCameraFunctions[idxOf] = VDEFunc.ref);
        break;
      case ACTIVATION_TYPE:
        idxOf = this.c300eosSettings!.vdeActivationFunctions.findIndex(
          (e: any) => e._key === VDEFunc.ref._key
        );
        idxOf === -1
          ? this.c300eosSettings!.vdeActivationFunctions.push(VDEFunc.ref)
          : (this.c300eosSettings!.vdeActivationFunctions[idxOf] = VDEFunc.ref);
        break;
      default:
        console.error(`Could not infer VDE function type. Breaking`);
        break;
    }
  }

  handleConfirmLanguage(selLanguage: Language) {
    if (!this.c300eosSettings) return;

    this.c300eosSettings.language = selLanguage;
  }

  addVdeFunction(
    title: string,
    enumObj: IntercomType | CameraType | ActivationType,
    type: string
  ) {
    let o: any = {
      title: title,
      ref: null,
      selectTypes: enumObj,
    };
    if (type === INTERCOM_TYPE) {
      o.ref = new IntercomFunction();
      o.ref.id = 0;
      o.type = INTERCOM_TYPE;
    }
    if (type === CAMERA_TYPE) {
      o.ref = new CameraFunction();
      o.ref.id = 0;
      o.type = CAMERA_TYPE;
    }
    if (type === ACTIVATION_TYPE) {
      o.ref = new ActivationFunction();
      o.ref.id = 0;
      o.type = ACTIVATION_TYPE;
    }

    this.selectedVdeFunction = o;
  }

  onSelectTab(tabValue: any) {
    this.selectedTab = tabValue;
  }

  onSelectVdeFunction(
    title: string,
    vdeFunction: any,
    objEnum: IntercomType | CameraType | ActivationType,
    type: string
  ) {
    this.selectedVdeFunction = {
      title: title,
      ref: vdeFunction,
      selectTypes: objEnum,
      type: type,
    };
  }

  toggleDeleteCameraConfirm(index: number) {
    if (!this.c300eosSettings || !this.c300eosSettings.vdeCameraFunctions)
      return;
    const a = [...this.c300eosSettings.vdeCameraFunctions];
    a.splice(index, 1);
    this.c300eosSettings.vdeCameraFunctions = a;
  }

  toggleDeleteItercomConfirm(index: number) {
    if (!this.c300eosSettings || !this.c300eosSettings.vdeIntercomFunctions)
      return;
    const a = [...this.c300eosSettings.vdeIntercomFunctions];
    a.splice(index, 1);
    this.c300eosSettings.vdeIntercomFunctions = a;
  }

  toggleDeleteActivationConfirm(index: number) {
    if (!this.c300eosSettings || !this.c300eosSettings.vdeActivationFunctions)
      return;
    const a = [...this.c300eosSettings.vdeActivationFunctions];
    a.splice(index, 1);
    this.c300eosSettings.vdeActivationFunctions = a;
  }

  async savePlantC300EOSSettings() {
    if (
      !this.currentPlant ||
      !this.currentPlant.desktopId ||
      !this.c300eosSettings
    )
      return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      this.removeTemporaryKeys();
      await plantsClient.c300eosSettingsPUT(
        this.currentPlant.desktopId,
        this.c300eosSettings,
        undefined
      );

      this.$emitter.emit(FETCH_PLANT);
      const summary = this.$t('general.synced');
      this.$toast.add({
        severity: 'success',
        summary: summary,
        closable: false,
        life: 3000,
      });
      this.isSettingsDirty = false;
    } catch (error) {
      switch ((error as any).status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  private removeTemporaryKeys() {
    if (!this.c300eosSettings) return;
    this.c300eosSettings.vdeCameraFunctions = this.c300eosSettings.vdeCameraFunctions?.map(
      (f) => {
        delete (f as any)._key;
        return f;
      }
    );
    this.c300eosSettings.vdeIntercomFunctions = this.c300eosSettings.vdeIntercomFunctions?.map(
      (f) => {
        delete (f as any)._key;
        return f;
      }
    );
    this.c300eosSettings.vdeActivationFunctions = this.c300eosSettings.vdeActivationFunctions?.map(
      (f) => {
        delete (f as any)._key;
        return f;
      }
    );
  }
}
